/**
 * VerdictOverridePopover — popover anchored to the Override button in
 * SectionCalibrationFooter. CEO picks a different verdict + optional reason,
 * then commits via the override endpoint.
 *
 * Authority:
 *   docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §15 — TC-DS-CAL-02
 *
 * Token bindings (zero hex):
 *   chips        → VERDICT_PILL.{strong, moderate, weak, unclear} + VERDICT_PILL.pill
 *   textarea     → ui/textarea.jsx + DS_TYPOGRAPHY.bodyMd + DS_COLORS.border.default
 *   buttons      → ACTION_BUTTONS.rerun (Cancel) + ACTION_BUTTONS primary navy fill (Save)
 *   labels       → DS_TYPOGRAPHY.actionBtn / sectionSubLabel / microLabel
 *   spacing      → DS_SPACING
 *   loading      → UI_BUTTON_STATE.LOADING
 *
 * Strings: AGENT_CONFIG.deal_evaluator.verdictOverridePopover from task-display-config.js.
 */

import React, { useState, useEffect } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Textarea } from '../ui/textarea';
import {
  ACTION_BUTTONS,
  DS_BORDERS,
  DS_COLORS,
  DS_SPACING,
  DS_TYPOGRAPHY,
  UI_BUTTON_STATE,
  VERDICT_PILL,
} from '../../config/ui-invariants';
import { AGENT_CONFIG } from '../../config/task-display-config';

const POP_CFG = AGENT_CONFIG.deal_evaluator.verdictOverridePopover;

// Each option binds its visual to the matching VERDICT_PILL token so the chip
// uses the exact frozen colors (no new tokens needed).
const VERDICT_TOKEN = {
  strong: VERDICT_PILL.strong,
  moderate: VERDICT_PILL.moderate,
  weak: VERDICT_PILL.weak,
  unclear: VERDICT_PILL.unclear,
};

const popoverContentStyle = {
  width: '320px',
  padding: `${DS_SPACING.cardPadding - 8}px`,
  background: DS_COLORS.surface.white,
  borderRadius: DS_BORDERS.radiusCard,
  border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
  boxShadow: DS_COLORS.shadow.card,
  display: 'flex',
  flexDirection: 'column',
  gap: `${DS_SPACING.labelToContent}px`,
};

const titleStyle = {
  ...DS_TYPOGRAPHY.microLabel,
  color: DS_COLORS.text.primary,
};

const chipsRowStyle = {
  display: 'flex',
  gap: `${DS_SPACING.buttonGap}px`,
  flexWrap: 'wrap',
};

function chipStyle(value, selected) {
  const token = VERDICT_TOKEN[value];
  return {
    background: selected ? token.background : DS_COLORS.surface.white,
    color: token.text,
    borderColor: selected ? token.text : DS_COLORS.border.default,
    borderWidth: VERDICT_PILL.pill.borderWidth,
    borderStyle: 'solid',
    borderRadius: VERDICT_PILL.pill.borderRadius,
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '14px',
    paddingRight: '14px',
    fontSize: VERDICT_PILL.pill.fontSize,
    fontWeight: VERDICT_PILL.pill.fontWeight,
    fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    minHeight: '36px',
    transition: 'background 150ms cubic-bezier(0.4, 0, 0.2, 1), border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  };
}

const reasonLabelStyle = {
  ...DS_TYPOGRAPHY.sectionSubLabel,
  color: DS_COLORS.text.secondary,
};

const textareaStyle = {
  ...DS_TYPOGRAPHY.bodyMd,
  fontSize: '14px',
  lineHeight: 1.5,
  color: DS_COLORS.text.body,
  borderColor: DS_COLORS.border.default,
  resize: 'none',
};

const buttonRowStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: `${DS_SPACING.buttonGap}px`,
  marginTop: '4px',
};

const cancelButtonStyle = {
  background: 'none',
  border: 'none',
  color: DS_COLORS.text.secondary,
  cursor: 'pointer',
  padding: '8px 14px',
  minHeight: `${DS_SPACING.minTouchTarget}px`,
  ...DS_TYPOGRAPHY.actionBtn,
};

const saveButtonBaseStyle = {
  background: DS_COLORS.text.brand,
  color: DS_COLORS.surface.white,
  border: `1.5px solid ${DS_COLORS.border.brand}`,
  borderRadius: DS_BORDERS.radiusButton,
  padding: '0 18px',
  minHeight: `${DS_SPACING.minTouchTarget}px`,
  cursor: 'pointer',
  ...DS_TYPOGRAPHY.actionBtn,
  transition: 'background 200ms cubic-bezier(0.4, 0, 0.2, 1)',
};

export default function VerdictOverridePopover({
  open,
  onOpenChange,
  onConfirm,
  isSaving,
  currentAgentVerdict,
  currentCeoVerdict,
  currentReason,
  triggerNode,
}) {
  const [selectedVerdict, setSelectedVerdict] = useState(currentCeoVerdict || null);
  const [reason, setReason] = useState(currentReason || '');

  // Reset internal state when popover opens.
  useEffect(() => {
    if (open) {
      setSelectedVerdict(currentCeoVerdict || null);
      setReason(currentReason || '');
    }
  }, [open, currentCeoVerdict, currentReason]);

  const canSave = Boolean(selectedVerdict) && !isSaving;

  const handleSave = () => {
    if (!canSave) return;
    onConfirm?.({
      verdict: selectedVerdict,
      reason: reason.trim() || null,
    });
  };

  const saveButtonStyle = canSave
    ? saveButtonBaseStyle
    : {
        ...saveButtonBaseStyle,
        opacity: UI_BUTTON_STATE.DISABLED.opacity,
        cursor: UI_BUTTON_STATE.DISABLED.cursor,
        pointerEvents: UI_BUTTON_STATE.DISABLED.pointerEvents,
      };

  const loadingSaveStyle = isSaving
    ? {
        opacity: UI_BUTTON_STATE.LOADING.opacity,
        cursor: UI_BUTTON_STATE.LOADING.cursor,
        pointerEvents: UI_BUTTON_STATE.LOADING.pointerEvents,
      }
    : {};

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{triggerNode}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        style={popoverContentStyle}
        data-testid="verdict-override-popover"
      >
        <div style={titleStyle}>{POP_CFG.title}</div>

        <div style={chipsRowStyle} role="radiogroup" aria-label={POP_CFG.title}>
          {POP_CFG.verdictOptions.map((opt) => {
            const selected = selectedVerdict === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setSelectedVerdict(opt.value)}
                style={chipStyle(opt.value, selected)}
                data-testid={`verdict-chip-${opt.value}`}
                data-selected={selected ? 'true' : 'false'}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <label style={reasonLabelStyle} htmlFor="verdict-override-reason">
          {POP_CFG.reasonLabel}
        </label>
        <Textarea
          id="verdict-override-reason"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={POP_CFG.reasonMaxChars}
          placeholder={POP_CFG.reasonPlaceholder}
          style={textareaStyle}
          data-testid="verdict-override-reason-input"
        />

        <div style={buttonRowStyle}>
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            disabled={isSaving}
            style={cancelButtonStyle}
            data-testid="verdict-popover-cancel"
          >
            {POP_CFG.cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            style={{ ...saveButtonStyle, ...loadingSaveStyle }}
            data-testid="verdict-popover-save"
          >
            {isSaving ? POP_CFG.loadingLabel : POP_CFG.confirmLabel}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
